import { FormEvent, useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { SpaceCategory, SpaceCategoryLabels } from "@lifeat/shared/data-models/virtual-space/SpaceCategory";
import { Routes } from "@lifeat/shared/system/RouteConstants";
import { isInternalEmail } from "@lifeat/shared/user/UserUtil";
import { BUTTON_CONTENT_CLASS, LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { LifeAtTextInputV2 } from "@common/LifeAtTextInput/LifeAtTextInputV2";
import { useUserContext } from "@contexts/UserContext";
import { callMonolith } from "@service/ServerlessFunctionsService";
import { Size } from "@themes/Device";
import Styles from "@themes/Styles";
import { buildRoute, isProductionRoute } from "@util/RouteUtil";
import { AdminCategoryInput } from "../../features/admin/AdminCategoryInput";
import { AdminError } from "../../features/admin/AdminError";
import UserAccountModal from "../../features/user/components/UserAccountModal/UserAccountModal";
import { SpaceCategoryIconResolver } from "../../features/virtual-space/components/SpaceCategoryIcon/SpaceCategoryIconResolver";
import LoadingView from "../../features/virtual-space/components/SpaceView/LoadingView/LoadingView";

const Root = styled.div`
    height: 100dvh;
    background: ${({ theme }) => theme.backgroundPrimary};
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow: auto;
`;

const Container = styled.main`
    padding: 24px;
    width: clamp(0px, ${Size.laptop}, 100%);
`;

const Title = styled.h1`
    margin-bottom: 12px;
`;

const SectionTitle = styled.h2`
    margin-bottom: 12px;
`;

const SearchGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    margin-bottom: 24px;

    @media (width > ${Size.laptop}) {
        grid-template-columns: 1fr 1fr;
    }
`;

const SearchForm = styled.form`
    display: flex;
    border: 1px solid ${({ theme }) => theme.borderPrimary};
    border-radius: ${Styles.borderRadiusSharp};
    align-items: center;
`;

const SearchInput = styled(LifeAtTextInputV2)`
    flex: 1;
`;

const CategoryContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 8px;
`;

const CategoryButton = styled(LifeAtButtonV2)`
    & .${BUTTON_CONTENT_CLASS} {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }
`;

const CategoryIcon = styled(SpaceCategoryIconResolver)`
    width: 100px;
    height: 100px;
`;

const PushToProdButton = styled(LifeAtButtonV2)`
    margin-bottom: 8px;
`;

const Description = styled.p`
    color: ${({ theme }) => theme.textColorSecondary};
`;

export function AdminPage() {
    const history = useHistory();
    const [email, setEmail] = useState("");
    const [uid, setUid] = useState("");
    const { loading, user } = useUserContext();
    const [isPushingToProd, setIsPushingToProd] = useState(false);

    const state = (() => {
        if (loading) {
            return "loading";
        }
        if (!user) {
            return "unauthenticated";
        }
        if (!isInternalEmail(user.email)) {
            return "unauthorized";
        }
        return "ready";
    })();

    const handleEmailSubmit = (e: FormEvent) => {
        e.preventDefault();
        history.push(`/admin/user-by-email/${btoa(email)}`);
    };

    const handleUidSubmit = (e: FormEvent) => {
        e.preventDefault();
        history.push(`/admin/user-by-uid/${uid}`);
    };

    const handlePushToProd = () => {
        setIsPushingToProd(true);
        callMonolith("admin-push-spaces-to-production", undefined)
            .then(() => alert("Space drop successful!"))
            .catch(() => alert("Failed to push spaces to prod."))
            .finally(() => {
                setIsPushingToProd(false);
            });
    };

    return (
        <Root>
            <Container>
                {state === "ready" && (
                    <>
                        <Title>Admin Dashboard</Title>
                        <SectionTitle>Find User</SectionTitle>
                        <SearchGrid>
                            <SearchForm onSubmit={handleEmailSubmit}>
                                <SearchInput
                                    placeholder="Enter email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                                <LifeAtButtonV2>🔍</LifeAtButtonV2>
                            </SearchForm>
                            <SearchForm onSubmit={handleUidSubmit}>
                                <SearchInput
                                    placeholder="Enter UID"
                                    value={uid}
                                    onChange={(e) => setUid(e.target.value)}
                                />
                                <LifeAtButtonV2>🔍</LifeAtButtonV2>
                            </SearchForm>
                        </SearchGrid>
                        <SectionTitle>Edit Spaces</SectionTitle>
                        {isProductionRoute() ? (
                            <Description>
                                Editing spaces in prod environment is a not a good idea. Please use the test environment
                                admin dashboard (
                                <a href="https://test.lifeat.app/admin" target="_blank">
                                    link
                                </a>
                                ), and use the "Push to Prod" button once you are ready to apply changes to the prod
                                environment.
                            </Description>
                        ) : (
                            <>
                                <CategoryContainer>
                                    {Object.values(SpaceCategory).map((category) => (
                                        <CategoryButton
                                            key={category}
                                            variant="outline"
                                            href={`/admin/space/${category}`}
                                        >
                                            <CategoryIcon category={category} />
                                            <span>{SpaceCategoryLabels[category]}</span>
                                        </CategoryButton>
                                    ))}
                                </CategoryContainer>
                                <AdminCategoryInput />
                                <PushToProdButton
                                    variant="inverseColor"
                                    isLoading={isPushingToProd}
                                    onClick={handlePushToProd}
                                >
                                    🚀 Push to Prod
                                </PushToProdButton>
                                <Description>
                                    ▲ This button applies the changes you made in test environment spaces to production
                                    (a.k.a. space drop). Make sure everything is working correctly on{" "}
                                    <a href="https://test.lifeat.app" target="_blank">
                                        test.lifeat.app
                                    </a>{" "}
                                    before you push this button!
                                </Description>
                            </>
                        )}
                    </>
                )}
                {state === "loading" && <LoadingView isAppLoading />}
                {state === "unauthenticated" && (
                    <UserAccountModal buttonText="Sign Up" onSuccessRedirectUrl={buildRoute(Routes.Admin)} />
                )}
                {state === "unauthorized" && <AdminError message="Keep out. You are not wanted here." />}
            </Container>
        </Root>
    );
}
